import { booleanIntersects } from '@turf/boolean-intersects';
import { kinks } from '@turf/kinks';
import { area } from '@turf/area';
import { union } from '@turf/union';
import type { Polygon } from 'geojson';

/** Reject invalid holes instead of silently repairing or filling them. */
export function validAreaTopology(polygon: Polygon): boolean {
  const outer = polygon.coordinates[0];
  if (!outer || !(area(polygon) > 0) || kinks(polygon).features.length) return false;
  const exterior: Polygon = { type: 'Polygon', coordinates: [outer] };
  const holes = polygon.coordinates.slice(1);
  for (let index = 0; index < holes.length; index += 1) {
    const hole = holes[index]!;
    if (
      !hole.length ||
      !booleanIntersects(exterior, { type: 'Point', coordinates: hole[0]! }) ||
      booleanIntersects(
        { type: 'LineString', coordinates: outer },
        { type: 'LineString', coordinates: hole },
      )
    )
      return false;
    for (let other = 0; other < index; other += 1) {
      if (
        booleanIntersects(
          { type: 'Polygon', coordinates: [hole] },
          { type: 'Polygon', coordinates: [holes[other]!] },
        )
      )
        return false;
    }
  }
  return true;
}

/** Parts may meet at vertices or nest in holes, but may not overlap or share edges. */
export function validAreaPartsTopology(polygons: readonly Polygon[]): boolean {
  if (!polygons.length || polygons.some((polygon) => !validAreaTopology(polygon))) return false;
  for (let index = 0; index < polygons.length; index += 1) {
    for (let other = 0; other < index; other += 1) {
      const a = polygons[index]!,
        b = polygons[other]!;
      if (!booleanIntersects(a, b)) continue;
      const merged = union({
        type: 'FeatureCollection',
        features: [a, b].map((geometry) => ({ type: 'Feature', properties: {}, geometry })),
      })?.geometry;
      if (!merged || merged.type !== 'MultiPolygon' || merged.coordinates.length !== 2)
        return false;
    }
  }
  return true;
}
